import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useContentSyncStore } from '@/store/modules/contentSync'
import { getCookie } from '@/utils/cookie'

export type GenerationTaskStatus = 'idle' | 'connecting' | 'generating' | 'monitoring' | 'completed' | 'failed' | 'connection_lost'

export interface GenerationTaskSnapshot {
  type: string
  prompt: string
  modelInfo: string
  files: string[]
  params: any
  /** 本次预计消耗积分（前端估价，历史项以后端 credit 为准） */
  credit?: number
}

interface ServerActiveTask {
  createdAt?: string
  taskId: string
  type: string
}

interface PersistedGenerationTask {
  ownerUserId: string
  savedAt: number
  snapshot: GenerationTaskSnapshot
  taskId: string
}

const ACTIVE_TASK_STORAGE_KEY = 'active-generation-task-v1'
const ACTIVE_TASK_MAX_AGE = 24 * 60 * 60 * 1000

export const useGenerationTaskStore = defineStore('generationTask', () => {
  const contentSyncStore = useContentSyncStore()
  const status = ref<GenerationTaskStatus>('idle')
  const snapshot = ref<GenerationTaskSnapshot | null>(null)
  const sessionId = ref('')
  const taskId = ref('')
  const taskStartedAt = ref(0)
  const results = ref<string[]>([])
  /** 后端回传的实际消耗积分（优先于前端估价） */
  const resultCredit = ref(0)
  const errorMessage = ref('')
  const unreadResult = ref(false)
  const historyDirty = ref<Record<string, number>>({ image: 0, video: 0, audio: 0 })

  const isRunning = computed(() => (
    status.value === 'connecting'
    || status.value === 'generating'
    || status.value === 'monitoring'
  ))

  function clearPersistedTask() {
    try {
      window.sessionStorage.removeItem(ACTIVE_TASK_STORAGE_KEY)
    }
    catch {
      // 浏览器禁用存储时仍保留当前页面内的任务追踪。
    }
  }

  function persistActiveTask() {
    if (!taskId.value || !snapshot.value) {
      return
    }
    try {
      const persisted: PersistedGenerationTask = {
        ownerUserId: getCookie('userId') || '',
        savedAt: taskStartedAt.value || Date.now(),
        snapshot: JSON.parse(JSON.stringify(snapshot.value)),
        taskId: taskId.value,
      }
      window.sessionStorage.setItem(ACTIVE_TASK_STORAGE_KEY, JSON.stringify(persisted))
    }
    catch {
      // 快照过大时至少保存恢复查询所需的任务号和类型。
      try {
        const fallback: PersistedGenerationTask = {
          ownerUserId: getCookie('userId') || '',
          savedAt: taskStartedAt.value || Date.now(),
          snapshot: {
            files: [],
            modelInfo: snapshot.value.modelInfo,
            params: { function: snapshot.value.type },
            prompt: snapshot.value.prompt,
            type: snapshot.value.type,
          },
          taskId: taskId.value,
        }
        window.sessionStorage.setItem(ACTIVE_TASK_STORAGE_KEY, JSON.stringify(fallback))
      }
      catch {
        // 隐私模式完全禁用存储时，不影响当前 WebSocket 生成流程。
      }
    }
  }

  function begin(task: GenerationTaskSnapshot) {
    // Pinia/ref 可能传入 Proxy；structuredClone(Proxy) 会抛 DataCloneError 并中断生成。
    snapshot.value = {
      credit: Number(task.credit) || 0,
      files: [...task.files],
      modelInfo: task.modelInfo,
      params: JSON.parse(JSON.stringify(task.params)),
      prompt: task.prompt,
      type: task.type,
    }
    status.value = 'connecting'
    sessionId.value = ''
    taskId.value = ''
    taskStartedAt.value = Date.now()
    results.value = []
    resultCredit.value = 0
    errorMessage.value = ''
    unreadResult.value = false
    clearPersistedTask()
  }

  function markGenerating() {
    status.value = 'generating'
    persistActiveTask()
  }

  function acceptTask(id: string) {
    if (!id) {
      return
    }
    taskId.value = id
    if (!taskStartedAt.value) {
      taskStartedAt.value = Date.now()
    }
    persistActiveTask()
  }

  function markMonitoring() {
    if (taskId.value) {
      status.value = 'monitoring'
      persistActiveTask()
    }
  }

  function setSessionId(id: string) {
    sessionId.value = id
  }

  function applyResultCredit(credit?: number) {
    const value = Number(credit)
    if (!Number.isFinite(value) || value <= 0) {
      return
    }
    resultCredit.value = value
    if (snapshot.value) {
      snapshot.value = {
        ...snapshot.value,
        credit: value,
      }
    }
  }

  function complete(urls: string[], credit?: number) {
    if (status.value === 'completed') {
      if (results.value.length === 0 && urls.length > 0) {
        results.value = [...urls]
      }
      applyResultCredit(credit)
      return
    }
    status.value = 'completed'
    results.value = [...urls]
    applyResultCredit(credit)
    unreadResult.value = true
    const type = snapshot.value?.type || 'image'
    historyDirty.value[type] = (historyDirty.value[type] || 0) + 1
    contentSyncStore.markAssetChanged(type)
    clearPersistedTask()
  }

  function fail(message: string) {
    status.value = 'failed'
    errorMessage.value = message
    unreadResult.value = true
    clearPersistedTask()
  }

  function discardActiveTask() {
    status.value = 'idle'
    snapshot.value = null
    sessionId.value = ''
    taskId.value = ''
    taskStartedAt.value = 0
    results.value = []
    resultCredit.value = 0
    errorMessage.value = ''
    unreadResult.value = false
    clearPersistedTask()
  }

  function markConnectionLost(message = '连接已断开，生成结果待确认') {
    if (status.value === 'connecting' || status.value === 'generating' || status.value === 'monitoring') {
      status.value = 'connection_lost'
      errorMessage.value = message
      persistActiveTask()
    }
  }

  function restorePersistedTask() {
    try {
      const raw = window.sessionStorage.getItem(ACTIVE_TASK_STORAGE_KEY)
      if (!raw) {
        return false
      }
      const persisted = JSON.parse(raw) as PersistedGenerationTask
      if (
        !persisted.taskId
        || !persisted.snapshot
        || !persisted.savedAt
        || !persisted.ownerUserId
        || persisted.ownerUserId !== (getCookie('userId') || '')
        || Date.now() - persisted.savedAt > ACTIVE_TASK_MAX_AGE
      ) {
        clearPersistedTask()
        return false
      }
      taskId.value = persisted.taskId
      taskStartedAt.value = persisted.savedAt
      snapshot.value = persisted.snapshot
      status.value = 'monitoring'
      results.value = []
      resultCredit.value = 0
      errorMessage.value = ''
      unreadResult.value = false
      return true
    }
    catch {
      clearPersistedTask()
      return false
    }
  }

  function restoreServerTask(task: ServerActiveTask) {
    if (!task.taskId) {
      return false
    }
    taskId.value = task.taskId
    taskStartedAt.value = Date.parse(task.createdAt || '') || Date.now()
    snapshot.value = {
      files: [],
      modelInfo: '',
      params: { function: task.type },
      prompt: '',
      type: task.type || 'image',
    }
    status.value = 'monitoring'
    results.value = []
    resultCredit.value = 0
    errorMessage.value = ''
    unreadResult.value = false
    persistActiveTask()
    return true
  }

  function consumeHistoryDirty(type: string) {
    const version = historyDirty.value[type] || 0
    historyDirty.value[type] = 0
    return version > 0
  }

  /** 后台任务完成时刷新历史，不影响当前进行中的任务状态 */
  function markHistoryDirty(type?: string) {
    const t = type || snapshot.value?.type || 'image'
    historyDirty.value[t] = (historyDirty.value[t] || 0) + 1
    contentSyncStore.markAssetChanged(t)
  }

  function markResultRead() {
    unreadResult.value = false
  }

  return {
    errorMessage,
    historyDirty,
    isRunning,
    resultCredit,
    results,
    sessionId,
    snapshot,
    status,
    taskId,
    taskStartedAt,
    unreadResult,
    acceptTask,
    begin,
    complete,
    consumeHistoryDirty,
    discardActiveTask,
    fail,
    markConnectionLost,
    markGenerating,
    markHistoryDirty,
    markMonitoring,
    markResultRead,
    restorePersistedTask,
    restoreServerTask,
    setSessionId,
  }
})
